import type { SuperHubStore } from '..';
import type { LintingResponseType } from '@readme/api/src/mappings/ai/types';
import type { StateCreator } from 'zustand';

import { current } from 'immer';

import { fetcher } from '@core/hooks/useReadmeApi';
import { actionLog } from '@core/store/util';

import { isStyleguideResults } from './util';

interface SuperHubLinterSliceState {
  /**
   * Whether the linter is currently running
   */
  isLoading: boolean;

  /**
   * Results from the last linter run, or null if no run has been performed yet
   */
  results: LintingResponseType['data'] | null;

  /**
   * Which view to show in the results UI. Can be one of:
   * - 'errors': show number of errors
   * - 'warnings': show number of warnings
   * - 'styleguide': show style guide score
   * - null: no view selected (e.g. immediately after running the linter)
   */
  resultsView: 'errors' | 'styleguide' | 'warnings' | null;
}

interface SuperHubLinterSliceAction {
  /**
   * Dismisses an error or warning lint result by index.
   */
  dismissResult: (kind: SuperHubLinterSliceState['resultsView'], index: number) => void;

  /**
   * Computes the average style guide score from each item in the style guide results.
   */
  getStyleguideTotalScore: () => number | null;

  /**
   * Clears the linter results and resets the view.
   */
  reset: () => void;

  /**
   * Runs the linter on the current document. This is an async action that
   * returns a promise that resolves when the linter has completed.
   */
  run: () => Promise<LintingResponseType['data']>;

  /**
   * Sets which view to show in the linter results UI.
   */
  setResultsView: (view: SuperHubLinterSliceState['resultsView']) => void;
}

export interface SuperHubLinterSlice {
  /**
   * State slice containing fields and actions that are relevant when using
   * the AI-powered linter for SuperHub doc pages.
   */
  linter: SuperHubLinterSliceAction & SuperHubLinterSliceState;
}

const initialState: SuperHubLinterSliceState = {
  isLoading: false,
  results: null,
  resultsView: null,
};

/**
 * SuperHubLinter state slice containing fields related to linting doc pages.
 */
export const createSuperHubLinterSlice: StateCreator<
  SuperHubLinterSlice & SuperHubStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  SuperHubLinterSlice
> = (set, get) => ({
  linter: {
    ...initialState,

    dismissResult: (kind, index) => {
      if (typeof kind !== 'string') return;

      set(
        state => {
          const results = state.linter.results?.[kind];
          if (!!results && !isStyleguideResults(current(results))) {
            results.splice(index, 1);
          }

          const areCurrentResultsEmpty = Array.isArray(results) && results.length === 0;
          const otherResults = kind === 'errors' ? 'warnings' : 'errors';
          const areOtherResultsEmpty = state.linter.results?.[otherResults].length === 0;

          if (areCurrentResultsEmpty && !areOtherResultsEmpty) {
            state.linter.resultsView = otherResults;
          } else if (areCurrentResultsEmpty && areOtherResultsEmpty) {
            const hasStyleguideResults = !!state.linter.results?.styleguide?.length;
            state.linter.resultsView = hasStyleguideResults ? 'styleguide' : null;
          }
        },
        false,
        actionLog('linter.dismissResult', { kind, index }),
      );
    },

    getStyleguideTotalScore: () => {
      const styleguideResults = get().linter.results?.styleguide;
      if (!styleguideResults || styleguideResults?.length === 0) return null;

      const averageScore = styleguideResults.reduce((acc, item) => acc + item.score, 0) / styleguideResults.length;
      const limitDecimals = Math.floor(averageScore * 100) / 100;
      return limitDecimals;
    },

    run: async () => {
      set(
        state => {
          state.linter.isLoading = true;
          state.linter.resultsView = null;
        },
        false,
        actionLog('linter.run.pending'),
      );

      const {
        document: { getApiUrl },
        slug,
      } = get();

      if (!slug) {
        throw new Error('Missing required slug');
      }

      try {
        const { data } = await fetcher<LintingResponseType>(`${getApiUrl({ slug })}/lint`, {
          method: 'POST',
          body: JSON.stringify({}),
        });

        set(
          state => {
            state.linter.isLoading = false;
            state.linter.results = data;
          },
          false,
          actionLog('linter.run.success'),
        );

        return data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching linter results:', error);

        set(
          state => {
            state.document.isSaving = false;
          },
          false,
          actionLog('linter.run.rejected', error),
        );

        throw error;
      }
    },

    setResultsView: view => {
      set(
        state => {
          state.linter.resultsView = view;
        },
        false,
        actionLog('linter.setResultsView', view),
      );
    },

    reset: () => {
      set(
        state => {
          state.linter.results = null;
          state.linter.resultsView = null;
        },
        false,
        actionLog('linter.reset'),
      );
    },
  },
});
